import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    Avatar,
    Typography,
    Button,
    IconButton,
    Grid,
    Paper,
    Stack,
    Divider,
    Chip
} from '@mui/material';
import { PictureAsPdf, Close, Edit } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { Citizen } from '../../types/citizen';
import api from '../../api/axios';

interface CitizenDetailsDialogProps {
    open: boolean;
    onClose: () => void;
    citizen: Citizen | null;
    onEdit?: (citizen: Citizen) => void;
}

const CitizenDetailsDialog: React.FC<CitizenDetailsDialogProps> = ({ open, onClose, citizen, onEdit }) => {
    const { data: politicalPartiesOptions } = useQuery({
        queryKey: ['political-parties-options'],
        queryFn: async () => {
            const res = await api.get('/tenant/options/political-parties');
            return res.data?.data || [];
        },
    });

    if (!citizen) return null;

    const partyColor = citizen.pro_party
        ? politicalPartiesOptions?.find((p: any) => p.name === citizen.pro_party)?.color_code
        : null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle sx={{
                background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 2
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ width: 48, height: 48, bgcolor: 'primary.main', fontWeight: 'bold' }}>
                        {citizen.name?.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box>
                        <Typography variant="h6" fontWeight="bold">
                            {citizen.name}
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.8, letterSpacing: 1 }}>
                            VOTER ID: {citizen.voter_id}
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {onEdit && (
                        <Button
                            onClick={() => onEdit(citizen)}
                            startIcon={<Edit />}
                            sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.3)', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}
                            variant="outlined"
                            size="small"
                        >
                            Edit
                        </Button>
                    )}
                    <Button
                        onClick={async () => {
                            try {
                                const res = await api.get(`/tenant/citizens/${citizen.id}/pdf`, { responseType: 'blob' });
                                const blob = new Blob([res.data], { type: 'application/pdf' });
                                const url = URL.createObjectURL(blob);
                                window.open(url, '_blank');
                            } catch {
                                alert('Failed to generate PDF');
                            }
                        }}
                        startIcon={<PictureAsPdf />}
                        sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.3)', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}
                        variant="outlined"
                        size="small"
                    >
                        Print
                    </Button>
                    <IconButton onClick={onClose} sx={{ color: 'white' }}>
                        <Close />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent sx={{ p: 2, bgcolor: '#f8fafc' }} id="printable-citizen-dialog">
                <Grid container spacing={2} className="print-root">
                    {/* Personal Info */}
                    <Grid size={{ xs: 12, md: 7 }} className="print-col-7">
                        <Paper sx={{ p: 2, borderRadius: 3, height: '100%', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
                            <Typography variant="subtitle2" color="primary" fontWeight="bold" sx={{ mb: 2, textTransform: 'uppercase' }}>
                                Personal Information
                            </Typography>
                            <Stack spacing={2}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">Age / Gender</Typography>
                                    <Typography variant="body2" fontWeight="medium" textTransform="capitalize">
                                        {citizen.age} Years / {citizen.gender}
                                    </Typography>
                                </Box>
                                <Divider />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">Date of Birth</Typography>
                                    <Typography variant="body2" fontWeight="medium">{citizen.dob || 'N/A'}</Typography>
                                </Box>
                                <Divider />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">Phone Number</Typography>
                                    <Typography variant="body2" fontWeight="medium">{citizen.phone || 'N/A'}</Typography>
                                </Box>
                                <Divider />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">WhatsApp</Typography>
                                    <Typography variant="body2" fontWeight="medium">{citizen.whatsapp_number || 'N/A'}</Typography>
                                </Box>
                                <Divider />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">Blood Group</Typography>
                                    <Typography variant="body2" fontWeight="medium">{citizen.blood_group || 'N/A'}</Typography>
                                </Box>
                                <Divider />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">Aadhaar Number</Typography>
                                    <Typography variant="body2" fontWeight="medium">{citizen.adhaar_number || 'N/A'}</Typography>
                                </Box>
                                <Divider />
                                <Box>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>Status</Typography>
                                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="caption" color="text.secondary">Social:</Typography>
                                            <Chip
                                                label={citizen.is_active_social ? "Yes" : "No"}
                                                color={citizen.is_active_social ? "success" : "default"}
                                                size="small"
                                                variant={citizen.is_active_social ? "filled" : "outlined"}
                                                sx={{ fontWeight: citizen.is_active_social ? 'bold' : 'normal', height: 24 }}
                                            />
                                        </Box>
                                        <Divider orientation="vertical" flexItem />
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="caption" color="text.secondary">NRI:</Typography>
                                            <Chip
                                                label={citizen.is_nri ? "Yes" : "No"}
                                                color={citizen.is_nri ? "info" : "default"}
                                                size="small"
                                                variant={citizen.is_nri ? "filled" : "outlined"}
                                                sx={{ fontWeight: citizen.is_nri ? 'bold' : 'normal', height: 24 }}
                                            />
                                        </Box>
                                        <Divider orientation="vertical" flexItem />
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="caption" color="text.secondary">Res. Stays:</Typography>
                                            <Chip
                                                label={citizen.is_resident_satys ? "Yes" : "No"}
                                                color={citizen.is_resident_satys ? "warning" : "default"}
                                                size="small"
                                                variant={citizen.is_resident_satys ? "filled" : "outlined"}
                                                sx={{ fontWeight: citizen.is_resident_satys ? 'bold' : 'normal', height: 24 }}
                                            />
                                        </Box>
                                    </Box>
                                </Box>
                            </Stack>
                        </Paper>
                    </Grid>

                    {/* Location & Status Info */}
                    <Grid size={{ xs: 12, md: 5 }} className="print-col-5">
                        <Paper sx={{ p: 2, borderRadius: 3, height: '100%', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
                            <Typography variant="subtitle2" color="primary" fontWeight="bold" sx={{ mb: 2, textTransform: 'uppercase' }}>
                                Location & Status
                            </Typography>
                            <Stack spacing={2}>
                                <Box>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>Administrative Location</Typography>
                                    <Grid container spacing={2}>
                                        <Grid size={{ xs: 12 }}>
                                            <Typography variant="caption" color="text.secondary" display="block">Constituency</Typography>
                                            <Chip label={citizen.constituency?.name || 'N/A'} size="small" variant="outlined" sx={{ width: '100%', justifyContent: 'flex-start', borderRadius: 1.5 }} />
                                        </Grid>
                                        <Grid size={{ xs: 12 }}>
                                            <Typography variant="caption" color="text.secondary" display="block">Village</Typography>
                                            <Chip label={citizen.village?.name || 'N/A'} size="small" variant="outlined" sx={{ width: '100%', justifyContent: 'flex-start', borderRadius: 1.5 }} />
                                        </Grid>
                                        <Grid size={{ xs: 12 }}>
                                            <Typography variant="caption" color="text.secondary" display="block">Voting Booth</Typography>
                                            <Chip label={citizen.voting_booth ? `${citizen.voting_booth.name} (#${citizen.voting_booth.booth_id})` : 'N/A'} size="small" variant="outlined" sx={{ width: '100%', justifyContent: 'flex-start', borderRadius: 1.5 }} />
                                        </Grid>
                                    </Grid>
                                </Box>
                                <Divider />
                                <Box>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>Detailed Address</Typography>
                                    <Typography variant="body2" fontWeight="medium">
                                        {citizen.address || 'No detailed address provided.'}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Paper>
                    </Grid>

                    {/* Professional Info & Metadata */}
                    <Grid size={{ xs: 12 }} className="print-col-12">
                        <Paper sx={{ p: 2, borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }} className='print-col-12'>
                            <Grid container spacing={2} className="print-root">
                                <Grid size={{ xs: 6, md: 3 }} className="print-col-3">
                                    <Typography variant="caption" color="text.secondary">Community</Typography>
                                    <Typography variant="body2" fontWeight="bold">{citizen.community || 'N/A'}</Typography>
                                </Grid>
                                <Grid size={{ xs: 6, md: 3 }} className="print-col-3">
                                    <Typography variant="caption" color="text.secondary">Profession</Typography>
                                    <Typography variant="body2" fontWeight="bold">{citizen.profession || 'N/A'}</Typography>
                                </Grid>
                                <Grid size={{ xs: 6, md: 3 }} className="print-col-3">
                                    <Typography variant="caption" color="text.secondary">Income</Typography>
                                    <Typography variant="body2" fontWeight="bold">{citizen.income || 'N/A'}</Typography>
                                </Grid>
                                <Grid size={{ xs: 6, md: 3 }} className="print-col-3">
                                    <Typography variant="caption" color="text.secondary">Pro Party</Typography>
                                    {citizen.pro_party ? (
                                        <Chip
                                            label={citizen.pro_party}
                                            size="small"
                                            sx={{
                                                mt: 0.5,
                                                fontWeight: 600,
                                                bgcolor: partyColor ? `${partyColor}20` : '#f3f4f6',
                                                color: partyColor || '#374151',
                                                border: partyColor ? `1px solid ${partyColor}50` : '1px solid #e5e7eb',
                                            }}
                                        />
                                    ) : (
                                        <Typography variant="body2" fontWeight="bold">N/A</Typography>
                                    )}
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>
                </Grid>
            </DialogContent>
        </Dialog>
    );
};

export default CitizenDetailsDialog;
